/** ONE board-matched read of an activity, shared by the Ties table and the
 *  Anchors tab so the two can't drift. A single horizontal line: colored code
 *  pill + the activity name (WRAPS, never ellipsizes) + optional dates, a
 *  relationship pill (FS/SS+lag), a status dot, and a quiet direction/origin
 *  marker. The static, non-input twin of one DepChipTypeahead row. */
import React from "react";
import { autoFontColor } from "../../../../utils/color";
import { formatDateMonthDay } from "../../../../utils/format";
import { formatLagSuffix } from "../../../../utils/p6";
import type { DepGraphPredType } from "../../../../models";
import "./ActivityLine.css";

export type ActivityStatusLite = "not_started" | "active" | "complete";
export type ActivityMarker = "in" | "out" | "both" | null;

interface Props {
  workCode: string;
  /** Primary label — already resolved via activityLabel() by the caller. */
  name: string;
  /** Raw P6 task_name — shown on hover when it differs from `name`. */
  fullName?: string;
  taskCode?: string;
  color: string;
  status?: ActivityStatusLite;
  startIso?: string | null;
  finishIso?: string | null;
  /** Relationship pill (driver/tie rows). */
  rel?: { type: DepGraphPredType; lagHours: number } | null;
  /** This tie is the binding (driving) dependency. */
  driving?: boolean;
  /** Anchor marker — a compact ⚓ IN/OUT pill (outside-tie direction). */
  marker?: ActivityMarker;
  /** Where it lives — "finished work" / another section's name. */
  origin?: string | null;
  /** Tie was severed by a delete / removal — struck + faded. */
  severed?: boolean;
  /** Compact: smaller, for nested driver / tie sub-rows. */
  dense?: boolean;
  className?: string;
}

const ActivityLine: React.FC<Props> = ({
  workCode,
  name,
  fullName,
  taskCode,
  color,
  status = "not_started",
  startIso,
  finishIso,
  rel,
  driving,
  marker,
  origin,
  severed,
  dense,
  className,
}) => {
  const title =
    fullName && fullName !== name ? `${taskCode ? taskCode + " · " : ""}${fullName}` : undefined;
  return (
    <span
      className={
        "activity-line" +
        (dense ? " activity-line--dense" : "") +
        (severed ? " activity-line--severed" : "") +
        (className ? ` ${className}` : "")
      }
      title={title}
    >
      {status === "active" && (
        <span className="activity-line-status activity-line-status--active" title="In progress" />
      )}
      {status === "complete" && (
        <span className="activity-line-lock" title="Completed" aria-hidden="true">
          🔒
        </span>
      )}
      {driving && (
        <span className="activity-line-driving" aria-hidden="true" title="Driving predecessor">
          ▸
        </span>
      )}
      {workCode ? (
        <span
          className="activity-line-code"
          style={{ background: color || undefined, color: color ? autoFontColor(color) : undefined }}
        >
          {workCode}
        </span>
      ) : (
        <span className="activity-line-code activity-line-code--none">—</span>
      )}
      <span className="activity-line-name">{name}</span>
      {taskCode && <span className="activity-line-taskcode">{taskCode}</span>}
      {(startIso || finishIso) && (
        <span className="activity-line-dates">
          {formatDateMonthDay(startIso ?? null)} – {formatDateMonthDay(finishIso ?? null)}
        </span>
      )}
      {rel && (
        <span className="activity-line-rel">
          {rel.type}
          {formatLagSuffix(rel.lagHours)}
        </span>
      )}
      {marker && (
        <span
          className={`activity-line-marker activity-line-marker--${marker}`}
          title="Anchor — tied to outside / finished work; persists through the pull"
        >
          {marker === "in" ? "IN" : marker === "out" ? "OUT" : "IN/OUT"}
        </span>
      )}
      {origin && <span className="activity-line-origin">↗ {origin}</span>}
    </span>
  );
};

export default ActivityLine;
